<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import SpacesAPI from 'dashboard/api/spaces';
import OnboardingSpaceCard from './OnboardingSpaceCard.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const props = defineProps({
  venueName: { type: String, default: '' },
  isPending: { type: Boolean, default: false },
});

const store = useStore();
const { t } = useI18n();

const attributeDefinitions = ref({});

const spaces = computed(() => store.state.onboarding.reviewSpaces);
const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);
const lindexError = computed(() => store.state.onboarding.lindexError);
const includedCount = computed(
  () => spaces.value.filter(space => space.included !== false).length
);

onMounted(async () => {
  try {
    const response = await SpacesAPI.getAttributeDefinitions();
    attributeDefinitions.value = response.data || {};
  } catch {
    attributeDefinitions.value = {};
  }
});

function meta(space) {
  return space.metadata || {};
}

function toggleIncluded(index) {
  const space = spaces.value[index];
  store.dispatch('onboarding/updateReviewSpace', {
    index,
    updates: { included: space.included === false },
  });
}

function updateName(index, name) {
  store.dispatch('onboarding/updateReviewSpace', {
    index,
    updates: { name },
  });
}

function updateCapacity(index, field, value) {
  const space = spaces.value[index];
  store.dispatch('onboarding/updateReviewSpace', {
    index,
    updates: {
      metadata: { ...meta(space), [field]: value },
    },
  });
}

function updateAttribute(index, key, value) {
  const space = spaces.value[index];
  store.dispatch('onboarding/updateReviewSpace', {
    index,
    updates: {
      custom_attributes: {
        ...(space.custom_attributes || {}),
        [key]: value,
      },
    },
  });
}

const continueSetup = () => store.dispatch('onboarding/createSpaces');
const skipSpaces = () => store.dispatch('onboarding/skipSpaces');

useRegisterOnboardingNav(() => ({
  secondary: {
    label: t('ONBOARDING_WIZARD.REVIEW.CONTINUE_WITHOUT'),
    loading: uiFlags.value.isCreatingSpaces || uiFlags.value.isProvisioning,
    onClick: skipSpaces,
  },
  primary: spaces.value.length
    ? {
        label: t('ONBOARDING_WIZARD.REVIEW.CTA'),
        disabled: includedCount.value === 0,
        loading:
          uiFlags.value.isCreatingSpaces || uiFlags.value.isProvisioning,
        onClick: continueSetup,
      }
    : null,
  hidePrimary: !spaces.value.length,
}));
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.PHASES.2.LABEL')"
    :title="t('ONBOARDING_WIZARD.REVIEW.TITLE', { venue: props.venueName })"
    :description="t('ONBOARDING_WIZARD.REVIEW.DESCRIPTION')"
    wide
  >
    <template v-if="spaces.length" #header-extra>
      <p class="m-0 text-sm text-slate-500 dark:text-slate-400">
        {{ t('ONBOARDING_WIZARD.REVIEW.TOGGLE_HINT') }}
      </p>
    </template>

    <div class="w-full flex flex-col gap-4">
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>

      <div
        v-if="isPending"
        class="w-full rounded-xl border border-slate-100 dark:border-slate-900 bg-white dark:bg-slate-900/40 shadow-sm px-6 py-12 text-left"
      >
        <div
          class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin mb-4"
        />
        <p class="text-base text-slate-500 dark:text-slate-400 m-0">
          {{ t('ONBOARDING_WIZARD.REVIEW.PENDING') }}
        </p>
      </div>

      <div
        v-else-if="!spaces.length"
        class="w-full flex flex-col items-start rounded-xl border border-slate-100 dark:border-slate-900 bg-white dark:bg-slate-900/40 shadow-sm px-6 py-12 text-left"
      >
        <div
          class="p-4 rounded-full mb-4 w-20 h-20 flex justify-center items-center"
          :class="
            lindexError
              ? 'bg-amber-50 dark:bg-amber-900/20'
              : 'bg-woot-50 dark:bg-woot-800'
          "
        >
          <Icon
            :icon="
              lindexError ? 'i-lucide-triangle-alert' : 'i-lucide-layout-grid'
            "
            class="size-8"
            :class="
              lindexError
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-woot-400'
            "
          />
        </div>
        <p class="text-base text-slate-500 dark:text-slate-400 mb-0 max-w-sm">
          {{
            lindexError
              ? t('ONBOARDING_WIZARD.REVIEW.FAILED')
              : t('ONBOARDING_WIZARD.REVIEW.EMPTY')
          }}
        </p>
      </div>

      <div v-else class="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
        <OnboardingSpaceCard
          v-for="(space, index) in spaces"
          :key="`${space.source_url}-${index}`"
          :space="space"
          :index="index"
          :attribute-definitions="attributeDefinitions"
          @toggle-included="toggleIncluded(index)"
          @update-name="name => updateName(index, name)"
          @update-capacity="
            (field, value) => updateCapacity(index, field, value)
          "
          @update-attribute="
            (key, value) => updateAttribute(index, key, value)
          "
        />
      </div>
    </div>
  </OnboardingStepLayout>
</template>
